import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { historyDate } from "@/lib/metrics/history";

const db = supabase as any;
type Row = { source: string; id: string; entry_date: string; label: string };

/** Coach/admin view of a member's dated history with delete. Deletes never reverse XP, coins or streaks. */
export default function StaffHistory({ memberId }: { memberId: string }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false);
  const q = useQuery({
    queryKey: ["staff-history", memberId],
    queryFn: async (): Promise<Row[]> => {
      const { data, error } = await db.rpc("staff_member_history", { _member: memberId });
      if (error) throw error; return data ?? [];
    },
  });
  const remove = async () => {
    if (!confirm) return;
    setBusy(true);
    const { error } = await db.rpc("staff_delete_history", { _source: confirm.source, _id: confirm.id });
    setBusy(false);
    if (error) return toast({ title: "Couldn’t delete", description: error.message, variant: "destructive" });
    toast({ title: "Entry deleted" });
    setConfirm(null);
    qc.invalidateQueries();
  };
  return (
    <details className="rounded-xl border border-border bg-card p-3">
      <summary className="cursor-pointer font-serif text-base">Dated history · {q.data?.length ?? 0}</summary>
      {q.error && <div role="alert" className="mt-2 flex items-center gap-2 text-xs">History couldn’t load.<Button size="sm" variant="outline" onClick={() => q.refetch()}>Retry</Button></div>}
      <ul className="mt-2 max-h-96 space-y-1 overflow-y-auto text-xs">
        {(q.data ?? []).map((r) => (
          <li key={r.source + r.id} className="border-b border-border py-1.5">
            <div className="flex items-center gap-2">
              <span className="w-24 shrink-0 text-muted-foreground">{historyDate(r.entry_date)}</span>
              <span className="min-w-0 flex-1 truncate">{r.label}</span>
              <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" aria-label={`Delete ${r.label}`} onClick={() => setConfirm(r)}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
            {confirm?.id === r.id && (
              <div role="alertdialog" className="mt-1 space-y-2 rounded-md border border-destructive/40 p-2">
                <p>Delete this member’s entry from {historyDate(r.entry_date)}? This can’t be undone.</p>
                <div className="flex gap-2"><Button size="sm" variant="outline" className="flex-1" disabled={busy} onClick={() => setConfirm(null)}>Keep it</Button><Button size="sm" variant="destructive" className="flex-1" disabled={busy} onClick={remove}>{busy ? "Deleting…" : "Delete"}</Button></div>
              </div>
            )}
          </li>
        ))}
        {q.data?.length === 0 && <li className="py-3 text-center text-muted-foreground">No dated entries yet</li>}
      </ul>
    </details>
  );
}
